import { CalendarItemType, MealType } from '@akin-home/amplify';
import {
  getMinuteValueFromTimeString,
  isValidTimeFormat,
} from '@akin-home/dates';
import {
  createRecurringItem,
  generateRecurrences,
} from '@akin-home/recurrence';
import { pipe } from '@akin-home/utils/fp';
import { addHours } from 'date-fns';
import { v4 as uuid } from 'uuid';
import { Meal } from '../types';

export * from './createMealFromRecipe';
export * from './groupMealsIntoWeekPlan';
export * from './importRecipeToMeal';
export * from './replaceMealRecipe';

export const createMealModel = (input: Partial<Meal>): Meal => ({
  id: uuid(),
  type: CalendarItemType.Meal,
  habitatID: '',
  recurrenceExcludeDates: {},
  title: '',
  participants: [],
  updatedAt: new Date().toISOString(),
  createdAt: new Date().toISOString(),
  mealType: MealType.Breakfast,
  ingredients: [],
  prepNotes: [],
  recipes: [],
  recipeM2m: [],
  eaten: false,
  ...getMealTypeTimes({ meal_type: input.mealType || MealType.Breakfast }),
  ...input,
});

export const getMealTypeStartTime = (
  mealType: MealType,
  date: Date | undefined,
  time?: string | undefined
): Date => {
  /** Create a copy of the date, since we are modifying it */
  const start = date ? new Date(date.toISOString()) : new Date();

  const minutes = isValidTimeFormat(time)
    ? getMinuteValueFromTimeString(time)
    : 0;

  start.setMinutes(minutes);
  const hour = isValidTimeFormat(time)
    ? parseInt(time.split(':')[0])
    : undefined;

  switch (mealType) {
    case 'Breakfast': {
      start.setHours(hour || 7);
      break;
    }
    case 'Lunch': {
      start.setHours(hour || 12);
      break;
    }
    case 'Dinner': {
      start.setHours(hour || 19);
      break;
    }
    case 'Snack': {
      start.setHours(hour || 15);
      break;
    }
  }

  return start;
};

interface MealTimeTypeParams {
  meal_type: MealType;
  date?: Date;
  time?: string;
}

export const getMealTypeTimes = (
  params: MealTimeTypeParams
): { start: Date; end: Date } => {
  const { meal_type, date, time } = params;

  const start = getMealTypeStartTime(meal_type, date, time);

  return {
    start,
    end: addHours(start, 1),
  };
};

export const generateMealRecurrences = generateRecurrences(
  ({ item, recurrenceDate }) => {
    return pipe(createRecurringItem)({ item, recurrenceDate });
  }
);

export const findMealByType = (meal_type: MealType) => (meals: Meal[]) => {
  return meals.find((meal) => meal.mealType === meal_type);
};

export const findBreakfastMeal = findMealByType(MealType.Breakfast);
export const findLunchMeal = findMealByType(MealType.Lunch);
export const findDinnerMeal = findMealByType(MealType.Dinner);
export const findSnackMeal = findMealByType(MealType.Snack);

export const isMeal = (item: { type: CalendarItemType }): item is Meal =>
  item.type === CalendarItemType.Meal;
